/**
 * 백오피스 그리드 필터 기억 (오더 관리 · 배송 관리).
 *
 * 리덕스 `order`·`shipment` 슬라이스는 persist 대상이 아니다(`persistConfig.ts` 의 주석 — 저장된
 * 상태를 되살리면 배포로 상태가 늘었을 때 죽는다). 그래서 **상태 트리가 아니라 값 몇 개만** 따로
 * 적어 둔다: 기준 · 시즌 · 상태(· 채널 · 종류). 화면 하나당 키 하나이고 서로 섞이지 않는다.
 *
 * 저장하지 않는 것 — 월 · 기간(시작·종료일) · 검색어. 다시 들어오면 이번 달, 검색어는 빈 칸이다.
 * 읽을 때 값을 검증하므로 옛 값이 남아 있어도 화면에 없는 조건으로 조회되지 않는다.
 */

export type BoFilterScope = 'order' | 'shipment'

/** 오더 관리는 date | season, 배송 관리는 month | date | season */
export type BoFilterMode = 'month' | 'date' | 'season'

export interface BoFilter {
	mode: BoFilterMode
	seasonId: string
	status: string
	/** all | retail | wholesale (DISPATCH_PLAN §5). 오더 관리 · 배송 관리 둘 다 */
	channel?: string
	/** 배송 관리만 — 종류(all | sale | wholesale | promo | repair | …) */
	kind?: string
}

const STORAGE_KEY: { [key in BoFilterScope]: string } = {
	order: 'judahs_bo_filter_order',
	shipment: 'judahs_bo_filter_shipment',
}

/** 화면에 실제로 있는 상태 값 — 여기 없는 값은 저장돼 있어도 버린다 */
const STATUS_WHITELIST: { [key in BoFilterScope]: string[] } = {
	order: [
		'all',
		'awaitingPayment',
		'new',
		'preparing',
		'inTransit',
		'completed',
		'refunded',
		'deleted',
	],
	shipment: [
		'all',
		'active',
		'preparing',
		'inTransit',
		'completed',
		'refunded',
		'deleted',
	],
}

const MODE_WHITELIST: { [key in BoFilterScope]: BoFilterMode[] } = {
	order: ['date', 'season'],
	shipment: ['month', 'date', 'season'],
}

const KIND_WHITELIST: string[] = [
	'all',
	'sale',
	'wholesale',
	'promo',
	'repair',
	'replacement',
	'support',
	'outsourcing',
	'inbound',
]

/**
 * 저장된 필터. 없거나 읽을 수 없으면 `null` 을 준다 — 부르는 쪽이 기본값을 쓴다.
 * 항목은 하나씩 검증한다(하나가 틀려도 나머지는 살린다).
 */
export const readBoFilter = (scope: BoFilterScope): Partial<BoFilter> | null => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY[scope])
		if (raw === null || raw === '') return null

		const parsed = JSON.parse(raw) as Partial<BoFilter>
		const result: Partial<BoFilter> = {}

		if (typeof parsed.mode === 'string' && MODE_WHITELIST[scope].includes(parsed.mode)) {
			result.mode = parsed.mode
		}
		if (typeof parsed.seasonId === 'string') {
			result.seasonId = parsed.seasonId
		}
		if (
			typeof parsed.status === 'string' &&
			STATUS_WHITELIST[scope].includes(parsed.status) &&
			// 배송 관리의 옛 기본값 'all' 은 버린다 — 2026-09-29 에 기본이 '진행중' 으로 바뀌었고,
			// 적혀 있던 'all' 을 되살리면 완료·환불·취소가 그대로 보여 바뀐 게 없어 보인다
			!(scope === 'shipment' && parsed.status === 'all')
		) {
			result.status = parsed.status
		}
		if (
			parsed.channel === 'all' ||
			parsed.channel === 'retail' ||
			parsed.channel === 'wholesale'
		) {
			result.channel = parsed.channel
		}
		if (
			scope === 'shipment' &&
			typeof parsed.kind === 'string' &&
			KIND_WHITELIST.includes(parsed.kind)
		) {
			result.kind = parsed.kind
		}

		return result
	} catch {
		return null
	}
}

/** 필터를 적어 둔다. 프라이빗 모드 등 저장이 막혀 있으면 조용히 넘어간다 */
export const writeBoFilter = (scope: BoFilterScope, filter: BoFilter) => {
	try {
		window.localStorage.setItem(STORAGE_KEY[scope], JSON.stringify(filter))
	} catch {
		// 저장 못 해도 이번 세션은 그대로 쓴다
	}
}
